import constants from '@Common/Core/Constant';
import { ThemeHelper } from '@Common/Helpers/ThemeHelper';
import {
    Background,
    BaseStyle,
    BorderStyle,
    GradientBackground,
    ImageBackground,
    InsightMetaModel,
    LinearGradientBackground,
    Padding,
    PaletteStyle,
    RadialGradientBackground,
    SolidBackground,
    TextStyle
} from '@InstanceFactory';
import Guid from '../Core/Guid';

import { properties } from '../Core/Properties';
import { ImageStretch, StyleElement } from '../Enums/Enum';
import ColorHelper from './ColorHelper';
import * as commonUtils from './CommonUtils';
import { enumerable } from '@Common/Core/CoreDecorator';
import { memoize } from '@Common/Core/MemoizeDecorator';

export interface ISVGStyle {
    strokeWidth: number;
    stroke: string;
    textAnchor: 'start' | 'middle' | 'end';
    fill: string;
    fontSize: string;
}

export default class StylesHelper {
    public static styleBlock: JQuery[];
    public readonly currentThemeId: string;

    constructor(themeId: string = StylesHelper.insightMeta.currentThemeId) {
        this.currentThemeId = themeId;
    }

    @enumerable
    private static get insightMeta(): InsightMetaModel {
        return (altosoft.model.insightMeta = altosoft.model.insightMeta || ({} as any));
    }

    @enumerable
    private static get allowedThemeIds(): string[] {
        return _.map(altosoft.model.user.allowedThemes, (name, themeId: string) => themeId).concat([Guid.empty]);
    }

    private static generateClassName(themeId: string, styleName: string): string {
        return `s${themeId}${styleName}`;
    }

    public static updateStyles(themeId: string, styles: BaseStyle[]): void {
        ColorHelper.resetCache(styles);
        _(styles).forEach(style => ColorHelper.translateColor(style));

        StylesHelper.insightMeta.stylesByTheme[themeId] = styles;
        StylesHelper.insightMeta.cssStyles = StylesHelper.getClientStyles();

        if (!_.isEmpty(StylesHelper.styleBlock)) {
            StylesHelper.styleBlock.forEach(i => i.remove());
        }

        StylesHelper.includeStyles();
    }

    public static styleToString(styleObject: BaseStyle): string {
        let result = '';
        if (BorderStyle.isInstanceOfType(styleObject)) {
            const { color, thickness, radius } = styleObject;
            result = `border-style:solid; border-color:${color}; border-width:${thickness}px; border-radius:${radius}px;`;
        } else if (TextStyle.isInstanceOfType(styleObject)) {
            result = styleObject.padding != null ? `padding:${this.paddingToString(styleObject.padding)};` : '';
            result += `text-align:${styleObject.alignType};`;
            if (styleObject.background) {
                result += `background:${StylesHelper.getBackground(styleObject.background)};`;
                result += `background-size:${StylesHelper.getBackgroundSize(styleObject.background)};`;
            }
            result += `font-weight:${styleObject.font.bold ? 'bold' : 'normal'};`;
            result += `color:${styleObject.font.color};`;
            result += `font-family:${StylesHelper.getFontFamily(styleObject.font.fontFamily)};`;
            result += `font-size:${styleObject.font.fontSize}px;`;
            result += `font-style:${styleObject.font.italic ? 'italic' : 'normal'};`;
            result += `text-decoration:${styleObject.font.underline ? 'underline' : 'none'};`;
        }
        return result;
    }

    public static paddingToString(padding: Padding, onlyHorizontal = false) {
        return `${onlyHorizontal ? 0 : padding.top}px ${padding.right}px ${onlyHorizontal ? 0 : padding.bottom}px ${padding.left}px`;
    }

    private static getStyleTextByTheme(themeId: string): string {
        const stylesHelper = new StylesHelper(themeId);
        return _.map(ThemeHelper.getStyles(themeId), style => stylesHelper.styleToClassString(style)).join(' ');
    }

    public static getEmptyStyle(): any {
        return {
            color: '',
            fontFamily: '',
            fontSize: '',
            fontStyle: '',
            fontWeight: '',
            textAlign: '',
            textDecoration: '',
            background: '',
            backgroundSize: '',
            padding: ''
        };
    }

    public static getClientStyles(): any[] {
        const cssStyles = [];
        StylesHelper.allowedThemeIds.map(themeId => {
            cssStyles.addRange(
                ThemeHelper.getStyles(themeId).map(style => {
                    let cssStyle = {
                        name: StylesHelper.generateClassName(themeId, style.name)
                    };
                    if (TextStyle.isInstanceOfType(style)) {
                        cssStyle = $.extend(
                            {
                                fontFamily: style.font ? StylesHelper.getFontFamily(style.font.fontFamily) : undefined,
                                fontStyle: style.font && style.font.italic ? 'italic' : 'normal',
                                fontSize: style.font ? style.font.fontSize + 'px' : undefined,
                                fontWeight: style.font && style.font.bold ? 'bold' : 'normal',
                                textDecoration: style.font && style.font.underline ? 'underline' : 'initial',
                                color: style.font ? style.font.color : undefined,
                                textAlign: style.alignType,
                                background: StylesHelper.getBackground(style.background),
                                backgroundSize: StylesHelper.getBackgroundSize(style.background),
                                padding: style.padding ? this.paddingToString(style.padding) : ''
                            },
                            cssStyle
                        );
                    } else if (BorderStyle.isInstanceOfType(style)) {
                        cssStyle = $.extend(
                            {
                                borderColor: style.color,
                                borderWidth: style.thickness,
                                borderRadius: style.radius,
                                borderStyle: style.thickness ? 'solid' : null
                            },
                            cssStyle
                        );
                    } else if (PaletteStyle.isInstanceOfType(style)) {
                        cssStyle = $.extend({ colors: style.palette }, cssStyle);
                    }
                    return cssStyle;
                })
            );
        });
        return cssStyles;
    }

    public styleToClassString(styleObject: BaseStyle): string {
        return `#${altosoft.stylePriorityId} .${this.getStyleName(styleObject.name)}{${StylesHelper.styleToString(styleObject)}}\n`;
    }

    public static getStylesText(): string {
        return _.map(StylesHelper.allowedThemeIds, themeId => this.getStyleTextByTheme(themeId)).join(' ');
    }

    public static includeStyles(): void {
        StylesHelper.styleBlock = _.map(StylesHelper.allowedThemeIds, themeId => {
            const style = document.createElement('style');
            style.type = 'text/css';
            style.appendChild(document.createTextNode(this.getStyleTextByTheme(themeId)));
            return $(style).appendTo('head');
        });
    }

    private static getClientStyleName(styleName: string): string {
        return (styleName || '').replace(/\W/g, '-');
    }

    public getStyleName(styleName: string, themeId: string = this.currentThemeId): string {
        return styleName ? StylesHelper.generateClassName(themeId, StylesHelper.getClientStyleName(styleName)) : '';
    }

    @memoize()
    public getStyle(styleName: string, backgroundOnly?: boolean): any {
        if (altosoft.model) {
            const styles = (altosoft.model.insightMeta && altosoft.model.insightMeta.cssStyles) || [];
            const className = StylesHelper.generateClassName(this.currentThemeId, styleName);
            let style = styles.find(cssStyle => cssStyle.name === className) || {};
            if (backgroundOnly) {
                style = {
                    background: style.background || '',
                    backgroundSize: style.backgroundSize || ''
                };
            }
            return style;
        }
        return null;
    }

    public getStylesByType(type: string): { id: string; name: string }[] {
        return _(ThemeHelper.getStyles(this.currentThemeId))
            .filter(style => style.$type === type)
            .map(style => ({
                id: this.getStyleName(style.name),
                name: style.name
            }))
            .value();
    }

    @memoize()
    public getOriginalStyle(styleName: string): BaseStyle {
        return _.find(ThemeHelper.getStyles(this.currentThemeId), cssStyle => cssStyle.name === styleName) || ({} as BaseStyle);
    }

    public getExtendedPalette(styleName: string): string[] {
        const paletteLength = 256;
        const defaultColors = constants.defaultChartPalette;

        let colors = _.filter(this.getStyle(styleName).colors || [], color => !_.isNil(color));
        while (colors.length < paletteLength) {
            colors = _.concat(colors, defaultColors);
        }
        colors.length = paletteLength;
        return _.map(colors, (color: any) => {
            return ColorHelper.getRepresentationColor(color);
        });
    }

    @memoize()
    public getSVGStyle(styleName: string, isThisStyleForLabel?: boolean): any {
        const style = commonUtils.clone(this.getStyle(styleName));
        const originalStyle = commonUtils.clone(this.getOriginalStyle(styleName));
        if (style.borderWidth) {
            style.strokeWidth = style.borderWidth;
        }
        if (style.borderColor) {
            style.stroke = style.borderColor;
        }
        if (style.textAlign && !isThisStyleForLabel) {
            style.textAnchor = style.textAlign.replace('Center', 'middle').replace('Right', 'end').replace('Left', 'start');
        }
        if (isThisStyleForLabel) {
            style.fill = style.color;
        } else if (TextStyle.isInstanceOfType(originalStyle)) {
            const background = originalStyle.background;
            if (SolidBackground.isInstanceOfType(background)) {
                style.fill = background.color;
            } else if (GradientBackground.isInstanceOfType(background)) {
                const backgroundObject: any = {
                    stops: _(background.gradientStops)
                        .orderBy(gradientStop => gradientStop.offset)
                        .map(gradientStop => [gradientStop.offset, gradientStop.color])
                        .value()
                };
                if (LinearGradientBackground.isInstanceOfType(background)) {
                    backgroundObject.linearGradient = [background.startX, background.startY, background.endX, background.endY];
                } else {
                    backgroundObject.radialGradient = { cx: 0.5, cy: 0.5, r: 0.7 };
                }
                style.fill = backgroundObject;
            }
        }
        style.fill = style.fill || 'transparent';
        delete style.borderWidth;
        delete style.borderColor;
        delete style.background;
        delete style.textAlign;
        return style;
    }

    public mergeStyles(style1: any, style2: any, backgroundOnly?: boolean): any {
        if (typeof style1 === 'string') {
            style1 = this.getStyle(style1);
        }
        if (typeof style2 === 'string') {
            style2 = this.getStyle(style2);
        }
        if (!style1) {
            return style2;
        }
        if (!style2) {
            return style1;
        }
        const backgrounds = [style2.background];
        const sizes = [style2.backgroundSize];
        // We cannot use multiple colors in background
        // INFO: http://tfs.kofax.com:8080/tfs/Products/Insight/_workitems/edit/651663
        if (!(style2.background && style2.background.startsWith('rgb') && style1.background && style1.background.startsWith('rgb'))) {
            backgrounds.push(style1.background);
            sizes.push(style1.backgroundSize);
        }
        return $.extend({}, style1, !backgroundOnly ? style2 : null, {
            background: backgrounds.filter(v => v).join(', '),
            backgroundSize: sizes.filter(v => v).join(', '),
            name: null
        });
    }

    public static getPartOfStyle(style: any, styleElements: number): any {
        const result: any = {};
        if (styleElements.hasFlag(StyleElement.background)) {
            result.background = style.background;
            result.backgroundSize = style.backgroundSize;
        }
        if (styleElements.hasFlag(StyleElement.color)) {
            result.color = style.color;
        }
        if (styleElements.hasFlag(StyleElement.textDecoration)) {
            result.textDecoration = style.textDecoration;
        }
        if (styleElements.hasFlag(StyleElement.fontWeight)) {
            result.fontWeight = style.fontWeight;
        }
        if (styleElements.hasFlag(StyleElement.fontStyle)) {
            result.fontStyle = style.fontStyle;
        }
        if (styleElements.hasFlag(StyleElement.fontSize)) {
            result.fontSize = style.fontSize;
        }
        return result;
    }

    @memoize()
    public static getFontFamily(fontFamily: string): string {
        const families = _([...fontFamily.split(', '), ...altosoft.fontInfo.fallback.split(', ')])
            .map(family => {
                if (family.match(/^(\w)+\s/g)) {
                    family = `"${family}"`;
                }
                return family;
            })
            .uniqBy(f => f.toLowerCase())
            .value();

        const isChinese = altosoft.locale === 'zh-hans';
        const indexSC = families.indexOf('"Noto Sans SC"');
        const indexJP = families.indexOf('"Noto Sans JP"');

        if ((isChinese && indexJP < indexSC) || (!isChinese && indexSC < indexJP)) {
            // Swap Japanese and Chinese
            [families[indexJP], families[indexSC]] = [families[indexSC], families[indexJP]];
        }

        return families.join(', ');
    }

    public static getBackgroundSize(background: Background): string {
        if (!background) {
            return 'auto';
        }
        if (ImageBackground.isInstanceOfType(background)) {
            return StylesHelper.getImageBackgroundSize(background.stretch);
        } else {
            return 'auto';
        }
    }

    public static getImageBackgroundSize(stretch: ImageStretch): string {
        switch (stretch) {
            case ImageStretch.fill:
                return '100% 100%';
            case ImageStretch.uniform:
                return 'contain';
            case ImageStretch.uniformToFill:
                return 'cover';
            case ImageStretch.none:
            default:
                return 'auto';
        }
    }

    public static getBackground(background: Background | any, namedColors?: IMap<string>): any {
        function getColor(color: string): string {
            return ColorHelper.getRepresentationColor(color, namedColors);
        }

        if (!background) {
            return undefined;
        }
        if (SolidBackground.isInstanceOfType(background)) {
            return getColor(background.color);
        }
        if (GradientBackground.isInstanceOfType(background)) {
            if (background.gradientStops.length < 2) {
                return 'none';
            }
            const isRadial = background.$type === RadialGradientBackground.typeName;
            let direction: string;
            if (LinearGradientBackground.isInstanceOfType(background)) {
                let horizontalDirection = '';
                if (background.startX < background.endX) {
                    horizontalDirection = 'left';
                } else if (background.startX > background.endX) {
                    horizontalDirection = 'right';
                }
                let verticalDirection = '';
                if (background.startY < background.endY) {
                    verticalDirection = 'top';
                } else if (background.startY > background.endY) {
                    verticalDirection = 'bottom';
                }
                direction = (verticalDirection + ' ' + horizontalDirection).trim();
            } else {
                const radialOrigin = _.get(background, 'radialOrigin', 'center');
                direction = radialOrigin + ', ellipse cover';
            }
            const gradientStops = _(background.gradientStops)
                .orderBy(gradientStop => gradientStop.offset)
                .map(gradientStop => getColor(gradientStop.color) + ' ' + gradientStop.offset * 100 + '%')
                .value()
                .join(', ');
            if (properties.cssPrefix) {
                if (properties.browser.msie && properties.browser.version < 10) {
                    // HACK ? which type supported for getIE9GradientBackground
                    return StylesHelper.getIE9GradientBackground(background as any, isRadial);
                } else {
                    return properties.cssPrefix + (!isRadial ? 'linear-gradient(' : 'radial-gradient(') + direction + ', ' + gradientStops + ')';
                }
            }
        }
        if (background.$type === ImageBackground.typeName) {
            return !background.data ? null : `url(${commonUtils.getImageSrc(background.data)}) no-repeat 50% 50%`;
        }
        return null;
    }

    public static getIE9GradientBackground(background: LinearGradientBackground, isRadial: boolean): string {
        const x1 = background.startX * 100;
        const x2 = background.endX * 100;
        const y1 = background.startY * 100;
        const y2 = background.endY * 100;
        const direction = isRadial ? 'cx="50%" cy="50%" r="75%"' : 'x1="{0}%" y1="{1}%" x2="{2}%" y2="{3}%"'.format(x1, y1, x2, y2);
        const gradientStops = _(background.gradientStops)
            .orderBy(gradientStop => gradientStop.offset)
            .map(gradientStop => `<stop offset="${gradientStop.offset * 100}%" stop-color="${gradientStop.color}" stop-opacity="1"/>`)
            .value()
            .join(' ');
        const gradientType = isRadial ? 'radialGradient' : 'linearGradient';
        const svgElement = commonUtils.base64Encode(
            `<?xml version="1.0" ?><svg xmlns="http://www.w3.org/2000/svg" width="100%" height="100%" viewBox="0 0 1 1" preserveAspectRatio="none"><${gradientType} id="grad-ucgg-generated" gradientUnits="userSpaceOnUse" ${direction}>${gradientStops}</${gradientType}><rect x="0" y="0" width="1" height="1" fill="url(#grad-ucgg-generated)" /></svg>`
        );
        return `url(data:image/svg+xml;base64,${svgElement})`;
    }

    public static imageFromText(text: string, parentElement: JQuery<HTMLElement>): string {
        let canvasElement = $('<canvas width="1024" height="1024"/>')[0] as HTMLCanvasElement;
        let context = canvasElement.getContext('2d');
        parentElement = $(parentElement);
        const fontSize = parentElement.css('fontSize');
        context.font = parentElement.css('fontStyle') + ' ' + fontSize + ' ' + parentElement.css('fontFamily');
        context.textBaseline = 'top';
        context.fillStyle = 'red'; // parentElement.css('color');
        context.fillText(text, 0, 0);
        const boundingBox = context.measureText(text);
        const width = boundingBox.width;
        const height = Math.ceil(parseInt(fontSize) * 1.14);
        const imageData = context.getImageData(0, 0, width, height);
        canvasElement.width = width;
        canvasElement.height = height;
        context.putImageData(imageData, 0, 0);
        const data = canvasElement.toDataURL('image/png');
        context = null;
        canvasElement = null;
        return data;
    }

    private static readonly isThemedClass = /^s[0-9a-z]{8}-[0-9a-z]{4}-[0-9a-z]{4}-[0-9a-z]{4}-[0-9a-z]{12}([\w-]*)$/i;

    private static getThemedStyleName(className: string): string {
        const groups = StylesHelper.isThemedClass.exec(className);
        if (groups) {
            return groups[1];
        }
        return null;
    }

    public applyThemeStyles(root: JQuery): void {
        const stylesByTheme = ThemeHelper.getStyles(this.currentThemeId);
        const styleNames = new Set(stylesByTheme.map(s => StylesHelper.getClientStyleName(s.name)));
        root.find('[class]').each((i, e) => {
            const element = $(e);
            let elementClasses = element.attr('class').split(' ');
            elementClasses = elementClasses.map(className => {
                className = StylesHelper.getThemedStyleName(className) || className;
                if (styleNames.has(className)) {
                    return this.getStyleName(className);
                }
                return className;
            });
            element.attr('class', elementClasses.join(' '));
        });
    }
}
